import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Brain, 
  MapPin, 
  Mail, 
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2
} from "lucide-react";
import QuickScoreBadge from "./QuickScoreBadge";

export interface ScoreRationale {
  skills?: {
    matched: number;
    total: number;
    matchedNames?: string[];
    missingNames?: string[];
  };
  experience?: {
    candidateYears: number;
    requiredYears: number;
  };
  location?: {
    candidateLocation: string;
    jobLocation: string;
    isMatch: boolean;
  };
  salary?: {
    candidateMin?: number;
    candidateMax?: number;
    jobMin?: number;
    jobMax?: number;
    currency?: string;
    isInRange: boolean;
  };
}

export interface SearchCandidate {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_primary: string | null;
  city: string | null;
  availability_status: string | null;
  ai_skills_summary: string | null;
  totalYearsExperience: number;
  matchedSkillsCount: number;
  requiredSkillsCount: number;
  quickScore: number;
  scoreBreakdown: {
    skills: number;
    experience: number;
    location: number;
    salary: number;
  };
  scoreRationale?: ScoreRationale;
  aiScore: number | null;
  aiScoredAt: string | null;
  aiReasoning?: string;
}

interface CandidateSearchResultsProps {
  candidates: SearchCandidate[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onPageChange: (page: number) => void;
  onAddToKanban: (candidateIds: string[]) => void;
  isScoring?: boolean;
  showOnlyHighScores?: boolean;
}

export default function CandidateSearchResults({
  candidates,
  total,
  page,
  limit,
  hasMore,
  selectedIds,
  onSelectionChange,
  onPageChange,
  onAddToKanban,
  isScoring = false,
  showOnlyHighScores = false,
}: CandidateSearchResultsProps) {
  const filteredCandidates = showOnlyHighScores
    ? candidates.filter(c => (c.aiScore ?? c.quickScore) >= 70)
    : candidates;

  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    onSelectionChange(newSelection);
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredCandidates.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(filteredCandidates.map(c => c.id)));
    }
  };

  const getInitials = (firstName: string | null, lastName: string | null) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  const getAvailabilityColor = (status: string | null) => {
    switch (status) {
      case 'available': return 'bg-green-500/20 text-green-700';
      case 'open': return 'bg-blue-500/20 text-blue-700';
      case 'not_looking': return 'bg-gray-500/20 text-gray-700';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-4">
      {/* Results summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {filteredCandidates.length} of {total} candidates
          {showOnlyHighScores && ` (filtered to ≥70% match)`}
        </p>
        
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <Button
              size="sm"
              onClick={() => onAddToKanban(Array.from(selectedIds))}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add to Kanban
            </Button>
          </div>
        )}
      </div>

      {/* Results table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={selectedIds.size === filteredCandidates.length && filteredCandidates.length > 0}
                  onCheckedChange={toggleAll}
                />
              </TableHead>
              <TableHead>Candidate</TableHead>
              <TableHead>Skills Match</TableHead>
              <TableHead>Experience</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-center">Quick Score</TableHead>
              <TableHead className="text-center">AI Score</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCandidates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {showOnlyHighScores 
                    ? "No candidates with ≥70% match found"
                    : "No candidates found matching your criteria"
                  }
                </TableCell>
              </TableRow>
            ) : (
              filteredCandidates.map((candidate) => (
                <TableRow key={candidate.id} className="group">
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(candidate.id)}
                      onCheckedChange={() => toggleSelection(candidate.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {getInitials(candidate.first_name, candidate.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">
                          {candidate.first_name} {candidate.last_name}
                        </p>
                        {candidate.email_primary && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {candidate.email_primary}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {candidate.matchedSkillsCount}/{candidate.requiredSkillsCount} skills
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
                      {candidate.totalYearsExperience} yrs
                    </div>
                  </TableCell>
                  <TableCell>
                    {candidate.city ? (
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                        {candidate.city}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <QuickScoreBadge
                      score={candidate.quickScore}
                      breakdown={candidate.scoreBreakdown}
                      rationale={candidate.scoreRationale}
                      isAiScore={false}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    {isScoring && selectedIds.has(candidate.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" />
                    ) : candidate.aiScore !== null ? (
                      <QuickScoreBadge
                        score={candidate.aiScore}
                        isAiScore={true}
                        aiReasoning={candidate.aiReasoning}
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100"
                      onClick={() => onAddToKanban([candidate.id])}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {Math.ceil(total / limit)}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={!hasMore}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
